<template>
  <q-dialog
    v-model="shown"
    maximized
    transition-show="fade-in"
    transition-hide="fade-out"
  >
    <q-card>
      <div class="row q-ma-sm justify-between">
        <div
          class="col-auto"
        />
        <div class="col-auto text-h6">
          Release Notes
        </div>
        <q-btn
          class="col-auto"
          icon="close"
          flat
          round
          dense
          data-test-id="close-release-notes"
          @click="shown = false"
        />
      </div>
      <q-card-section>
        <div
          class="row q-col-gutter-md column-container"
          style=""
        >
          <div
            class="col-12 col-sm-4"
          >
            <q-list
              bordered
              separator
              :class="$q.screen.lt.sm ? 'stacked-column-33' : 'side-column'"
            >
              <q-item
                v-for="version in clientVersions.data"
                :key="version.id"
                clickable
                :class="clientVersionMenuClasses(version)"
                @click="selectClientVersion(version)"
              >
                <q-item-section>
                  <div
                    class="col-auto"
                  >
                    {{ version.title }}
                  </div>
                  <div
                    class="text-caption col-auto"
                  >
                    {{ version.version }}
                    <template v-if="version.releaseNotesPublishedAt">
                      - {{ $d(version.releaseNotesPublishedAt, 'short') }}
                    </template>
                    <q-icon
                      v-if="currentUser && currentUser.superUser"
                      name="public"
                    >
                      <q-tooltip>{{ regions(version) }}</q-tooltip>
                    </q-icon>
                  </div>
                </q-item-section>
                <div class="q-mt-sm">
                  <q-icon
                    v-if="version.indyOnly"
                    name="visibility_off"
                    color="negative"
                    class="text-negative"
                    size="sm"
                  >
                    <q-tooltip>Visible only to INDY Super Users</q-tooltip>
                  </q-icon>
                  <q-icon
                    v-if="version.urgent"
                    name="priority_high"
                    class="text-negative"
                    size="sm"
                  >
                    <q-tooltip>Urgent, update applied outside of maintenance window</q-tooltip>
                  </q-icon>
                  <q-icon
                    v-if="version.hasReleaseNotes && version.releaseNotesPublished && version.important"
                    name="description"
                    class="text-warning"
                    size="sm"
                  >
                    <q-tooltip>Important Release Notes</q-tooltip>
                  </q-icon>
                  <q-icon
                    v-else-if="version.hasReleaseNotes && version.releaseNotesPublished"
                    name="description"
                    class="text-responsive-grey-7"
                    size="sm"
                  >
                    <q-tooltip>Has Release Notes</q-tooltip>
                  </q-icon>
                  <q-icon
                    v-if="version.isComingSoon($versionManager.comparableCurrentVersion)"
                    name="lock"
                    color="red-7"
                    size="sm"
                  >
                    <q-tooltip>This feature is not yet released and is coming soon</q-tooltip>
                  </q-icon>
                </div>
              </q-item>
            </q-list>
          </div>
          <div
            class="col-12 col-sm-8"
          >
            <div
              ref="releaseNotesContainer"
              class="scroll q-pa-lg smooth-scroll"
              style="border: solid 1px #EEE; border-radius: 2px;"
              :class="$q.screen.lt.sm ? 'stacked-column-66' : 'side-column'"
            >
              <!-- eslint-disable vue/no-v-html -->
              <!-- v-html produced by superusers only -->
              <div
                v-if="clientVersion.releaseNotes && clientVersion.releaseNotesPublished"
                class="internal-documentation"
                v-html="clientVersion.releaseNotes"
              />
              <!-- eslint-enable vue/no-v-html -->
              <div v-else>
                No Release Notes For Version {{ clientVersion.version }}
              </div>
              <q-inner-loading :showing="clientVersion.loading" />
            </div>
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import { appMode } from 'src/config'
import { REGIONS } from 'src/models/developer/Developer'
import User from 'src/models/user/User'

export default {
  name: 'ReleaseNotesDialog',
  context: ['currentUser'],
  models: {
    clientVersions: {
      regions: true,
      variables () {
        return {
          currentVersion: this.$versionManager.currentVersion,
          appMode,
          type: 'release-notes',
          limit: 100
        }
      },
      immediate: false,
      watchQuery: false
    },
    clientVersion: {
      releaseNotes: 'default',
      variables () {
        return {
          id: this.clientVersionId
        }
      }
    }
  },
  emits: ['update-release-notes-count'],
  data () {
    return {
      shown: false,
      clientVersionId: null
    }
  },
  computed: {
    unseenClientVersions () {
      return _.filter(this.clientVersions.data, version =>
        version.hasReleaseNotes &&
        version.releaseNotesPublished &&
        version.isUnseen(this.currentUser.seenClientVersion)
      )
    },
    unseenReleasedClientVersions () {
      return _.filter(this.unseenClientVersions, version =>
        !version.isComingSoon(this.$versionManager.comparableCurrentVersion)
      )
    },
    unseenImportantClientVersions () {
      return _.filter(this.unseenReleasedClientVersions, 'important')
    },
    firstUnseenImportantClientVersion () {
      return _.last(this.unseenImportantClientVersions)
    },
    firstUnseenClientVersion () {
      return _.last(this.unseenReleasedClientVersions)
    },
    unseenCount () {
      return this.unseenClientVersions.length
    },
    unseenImportantCount () {
      return this.unseenImportantClientVersions.length
    }
  },
  watch: {
    shown () {
      if (this.shown) return

      // This unselects a specific value and triggers an update to the url
      this.clientVersionId = null
    },
    'clientVersion.id' () {
      if (!this.clientVersion) return

      this.updateUserSeenClientVersion()

      this.$refs.releaseNotesContainer && (this.$refs.releaseNotesContainer.scrollTop = 0)

      this.$trackPage(
        this,
        {
          id: this.clientVersion.id,
          clientVersion: this.clientVersion.version,
          title: this.clientVersion.title
        }
      )
    },
    '$route.query.client_version_id': {
      handler () {
        if (this.$route.query.client_version_id) {
          this.show()
        }
      },
      immediate: true
    },
    clientVersionId () {
      if (this.clientVersionId !== this.$route.query.client_version_id) {
        this.updateRoute()
      }
    },
    unseenCount: {
      handler () {
        this.$emit('update-release-notes-count', this.unseenImportantCount || this.unseenCount)
      },
      immediate: true
    }
  },
  mounted () {
    this.showOnImportantUpdate()
  },
  methods: {
    async show () {
      this.shown = true
      await this.clientVersions.fetch(true)
      if (!this.$route.query.client_version_id) {
        this.selectInitialClientVersion()
      } else {
        this.clientVersionId = this.$route.query.client_version_id
      }
    },
    async showOnImportantUpdate () {
      // We can skip this check if we know we need to show
      if (this.$route.query.client_version_id) return

      await this.currentUser.ensureLoaded()

      // Only show release notes if there is a current user. This also prevents showing
      // release notes when there is an error loading context
      if (!this.currentUser || this.currentUser.isLocal) return

      await this.clientVersions.ensureLoaded()

      if (!this.firstUnseenImportantClientVersion) return

      this.show()
    },
    selectInitialClientVersion () {
      this.selectClientVersion(
        this.firstUnseenImportantClientVersion ||
        this.firstUnseenClientVersion ||
        _.last(this.unseenReleasedClientVersions) ||
        _.first(this.clientVersions.data)
      )
    },
    async selectClientVersion (version) {
      this.clientVersionId = version.id
    },
    updateUserSeenClientVersion () {
      if (!this.clientVersion?.isRemote) return

      let clientVersionComparableVersion = this.clientVersion.comparableVersion
      if (clientVersionComparableVersion > this.$versionManager.comparableCurrentVersion) {
        clientVersionComparableVersion = this.$versionManager.comparableCurrentVersion
      }

      if (this.currentUser.seenClientVersion && this.currentUser.seenClientVersion >= clientVersionComparableVersion) return

      const user = new User({
        id: this.currentUser.id,
        seenClientVersion: clientVersionComparableVersion
      }, {
        fields: ['id', 'seenClientVersion']
      })
      user.save()
    },
    clientVersionMenuClasses (version) {
      const classes = []

      if (version.isComingSoon(this.$versionManager.comparableCurrentVersion)) {
        if (this.clientVersionId === version.id) {
          classes.push('bg-responsive-grey-5 text-responsive-grey-7')
        } else {
          classes.push('bg-responsive-grey-4 text-responsive-grey-6')
        }
      } else {
        if (this.clientVersionId === version.id) {
          classes.push('bg-responsive-grey-4')
        } else {
          classes.push('bg-responsive-grey-1')
        }
      }

      if (version.isUnseen(this.currentUser.seenClientVersion)) {
        classes.push('text-bold')
      }

      return classes.join(' ')
    },
    regions (version) {
      if (version.regions.length === REGIONS.ALL.length) return 'ALL'

      return version.regions.join(', ')
    },
    updateRoute () {
      let query = { ...this.$route.query }
      query.client_version_id = this.clientVersionId
      query = _.omitBy(query, _.isNil)

      history.pushState(
        {},
        null,
        '#' + this.$route.path + '?' +
          _(query).keys().map(key => encodeURIComponent(key) + '=' + encodeURIComponent(query[key])).value().join('&')
      )
    }
  }
}

</script>

<style lang="scss" scoped>
.column-container {
  max-height: -webkit-calc(100vh - 70px);
  max-height: -moz-calc(100vh - 70px);
  max-height: calc(100vh - 70px);
  overflow: hidden;
}
.stacked-column-33 {
  max-height: -webkit-calc(33vh - 25px);
  max-height: -moz-calc(33vh - 25px);
  max-height: calc(33vh - 25px);
  overflow: auto;
}
.stacked-column-66 {
  max-height: -webkit-calc(66vh - 70px);
  max-height: -moz-calc(66vh - 70px);
  max-height: calc(66vh - 70px);
  overflow: auto;
}
.side-column {
  max-height: -webkit-calc(100vh - 90px);
  max-height: -moz-calc(100vh - 90px);
  max-height: calc(100vh - 90px);
  overflow: auto;
}
.smooth-scroll {
  scroll-behavior: smooth;
}
</style>
